import { supabase } from "@/integrations/supabase/client";
import type { ParkingListing, ParkingType, VehicleType } from "@/lib/parking-mock-data";
import { MOCK_PARKING } from "@/lib/parking-mock-data";

export interface DbParking {
  id: string;
  title: string;
  provider_name: string | null;
  description: string | null;
  address: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  location_precision: "exact" | "approximate";
  parking_type: ParkingType;
  vehicle_types: string[];
  total_slots: number;
  available_slots: number;
  price_hourly: number | null;
  price_daily: number | null;
  price_monthly: number | null;
  opening_time: string | null;
  closing_time: string | null;
  open_24h: boolean;
  cctv: boolean;
  security: boolean;
  lighting: boolean;
  ev_charging: boolean;
  entry_instructions: string | null;
  exit_instructions: string | null;
  image_url: string | null;
  booking_enabled: boolean;
  verified: boolean;
  status: string;
  created_at?: string | null;
}

const SELECT =
  "created_at,id,title,provider_name,description,address,city,latitude,longitude,location_precision,parking_type,vehicle_types,total_slots,available_slots,price_hourly,price_daily,price_monthly,opening_time,closing_time,open_24h,cctv,security,lighting,ev_charging,entry_instructions,exit_instructions,image_url,booking_enabled,verified,status";

export async function fetchPublishedParking(): Promise<DbParking[]> {
  const { data, error } = await supabase
    .from("parking_listings")
    .select(SELECT)
    .eq("status", "published")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as DbParking[]) ?? [];
}

export async function fetchMyParking(ownerId: string): Promise<DbParking[]> {
  const { data, error } = await supabase
    .from("parking_listings")
    .select(SELECT)
    .eq("owner_id", ownerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as unknown as DbParking[]) ?? [];
}

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1590674899484-d5640e854abe?auto=format&fit=crop&w=800&q=70";

/** Project real lat/lng into the faux-map viewport (%), same maths as properties. */
export function projectParkingToViewport(rows: DbParking[]): ParkingListing[] {
  const withCoords = rows.filter((r) => r.latitude != null && r.longitude != null);
  const minLat = Math.min(...withCoords.map((r) => r.latitude as number));
  const maxLat = Math.max(...withCoords.map((r) => r.latitude as number));
  const minLng = Math.min(...withCoords.map((r) => r.longitude as number));
  const maxLng = Math.max(...withCoords.map((r) => r.longitude as number));
  const latSpan = maxLat - minLat || 0.01;
  const lngSpan = maxLng - minLng || 0.01;

  return rows.map((r, i): ParkingListing => {
    let x = 18 + ((i * 113) % 64);
    let y = 18 + ((i * 71) % 64);
    if (r.latitude != null && r.longitude != null) {
      x = 10 + ((r.longitude - minLng) / lngSpan) * 80;
      y = 90 - ((r.latitude - minLat) / latSpan) * 80;
    }
    return {
      id: r.id,
      title: r.title,
      providerName: r.provider_name ?? "—",
      address: r.address ?? "",
      city: r.city ?? "",
      distanceKm: 0,
      locationPrecision: r.location_precision,
      parkingType: r.parking_type,
      vehicleTypes: (r.vehicle_types ?? []) as VehicleType[],
      totalSlots: r.total_slots,
      availableSlots: r.available_slots,
      priceHourly: r.price_hourly != null ? Number(r.price_hourly) : null,
      priceDaily: r.price_daily != null ? Number(r.price_daily) : null,
      priceMonthly: r.price_monthly != null ? Number(r.price_monthly) : null,
      openingTime: r.opening_time,
      closingTime: r.closing_time,
      open24h: r.open_24h,
      cctv: r.cctv,
      security: r.security,
      lighting: r.lighting,
      evCharging: r.ev_charging,
      verified: r.verified,
      image: r.image_url ?? FALLBACK_IMG,
      description: r.description ?? "",
      entryInstructions: r.entry_instructions ?? undefined,
      exitInstructions: r.exit_instructions ?? undefined,
      bookingEnabled: r.booking_enabled,
      x,
      y,
      lat: r.latitude ?? undefined,
      lng: r.longitude ?? undefined,
      createdAt: r.created_at ?? undefined,
    };
  });
}

/**
 * Live parking when the database has published rows, otherwise the
 * demonstration set so the map is never empty during preview.
 */
export function parkingOrDemo(rows: DbParking[]): ParkingListing[] {
  return rows.length ? projectParkingToViewport(rows) : MOCK_PARKING;
}

export function parkingPriceLabel(p: ParkingListing): string {
  if (p.priceHourly != null) return `₹${p.priceHourly} / hr`;
  if (p.priceDaily != null) return `₹${p.priceDaily} / day`;
  if (p.priceMonthly != null) return `₹${p.priceMonthly} / mo`;
  return "On request";
}
